import React from 'react';
import { Select as MuiSelect, MenuItem, Checkbox, styled } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { Colors } from 'library';

type MultiSelectTone = 'primary' | 'muted' | 'error';

interface MultiSelectOption {
    key: string;
    value: string;
}

interface MultiSelectProps {
    value: string[];
    onChange: (value: string[]) => void;
    onClose?: () => void;
    options: MultiSelectOption[];
    placeholder?: string;
    allLabel?: string;
    renderSummary?: (selectedLabels: string[]) => React.ReactNode;
    disabled?: boolean;
    fullWidth?: boolean;
    tone?: MultiSelectTone;
}

const TONE_BG: Record<MultiSelectTone, string> = {
    primary: Colors.white,
    muted: Colors.surface,
    error: Colors.errorBackground,
};

const TONE_BORDER: Record<MultiSelectTone, string> = {
    primary: Colors.border,
    muted: Colors.border,
    error: Colors.error,
};

const TONE_FOCUS_BORDER: Record<MultiSelectTone, string> = {
    primary: Colors.textSecondary,
    muted: Colors.textSecondary,
    error: Colors.error,
};

export function MultiSelect({
    value,
    onChange,
    onClose,
    options,
    placeholder,
    allLabel = 'All',
    renderSummary,
    disabled,
    fullWidth,
    tone = 'muted',
}: MultiSelectProps) {
    return (
        <StyledSelect
            multiple
            value={value}
            onChange={(e) => {
                const selected = e.target.value;
                onChange(typeof selected === 'string' ? selected.split(',') : (selected as string[]));
            }}
            onClose={onClose}
            disabled={disabled}
            fullWidth={fullWidth}
            displayEmpty
            IconComponent={StyledExpandMore}
            $tone={tone}
            MenuProps={{
                disableScrollLock: true,
                PaperProps: {
                    sx: {
                        borderRadius: '10px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        marginTop: '4px',
                        maxHeight: 350,
                    },
                },
            }}
            renderValue={(selected) => {
                const values = selected as string[];
                if (!values.length) {
                    return placeholder ? <Placeholder>{placeholder}</Placeholder> : allLabel;
                }
                const labels = values.map((key) => options.find((o) => o.key === key)?.value ?? key);
                return renderSummary ? renderSummary(labels) : labels.join(', ');
            }}
        >
            {options.map((opt) => (
                <StyledMenuItem key={opt.key} value={opt.key}>
                    <StyledCheckbox checked={value.includes(opt.key)} size="small" />
                    {opt.value}
                </StyledMenuItem>
            ))}
        </StyledSelect>
    );
}

const StyledExpandMore = styled(ExpandMore)({
    fontSize: 20,
    color: Colors.textSecondary,
});

const StyledSelect = styled(MuiSelect, { shouldForwardProp: (p) => p !== '$tone' })<{ $tone: MultiSelectTone }>(
    ({ $tone }) => ({
        fontSize: 14,
        color: Colors.primary,
        backgroundColor: TONE_BG[$tone],
        borderRadius: 10,
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: TONE_BORDER[$tone],
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: TONE_BORDER[$tone],
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: TONE_FOCUS_BORDER[$tone],
            borderWidth: 1,
        },
        '&.Mui-disabled': {
            backgroundColor: Colors.fill,
            color: Colors.textSecondary,
        },
        '& .MuiSelect-select': {
            padding: '10px 14px',
        },
    }),
);

const StyledMenuItem = styled(MenuItem)({
    fontSize: 14,
    padding: '3px 14px 3px 6px',
    margin: '0 8px',
    minHeight: 'unset',
    borderRadius: 8,
    '&.Mui-selected': {
        backgroundColor: 'transparent',
        color: Colors.primary,
    },
    '&.Mui-selected:hover': {
        backgroundColor: Colors.surface,
    },
    '&:hover': {
        backgroundColor: Colors.surface,
    },
});

const StyledCheckbox = styled(Checkbox)({
    padding: '4px 8px 4px 4px',
    color: Colors.border,
    '&.Mui-checked': {
        color: Colors.secondary,
    },
});

const Placeholder = styled('span')({
    color: Colors.border,
});
